import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { isValid, parseISO } from 'date-fns';
import isEqual from 'lodash/isEqual';
import { ReactElement, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { useStoreActions } from '~/store/hooks';
import { toDay } from '~/utils/date/date';
import { extractErrorCode, extractErrorMessage } from '~/utils/error/error';
import {
  ICeExemption,
  ICeExemptionCategory,
  ICeMonthRequest,
} from '~/services/api/communityEngagement/types';
import CeErrorCode from '~/ui/pages/Clients/reusable/CommunityEngagement/constants/CeErrorCode';
import Button from '~/ui/components/common/Button';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import Modal from '~/ui/components/common/Modal';
import FormSelect from '~/ui/components/form/FormSelect';
import DatePicker from '~/ui/components/inputs/DatePicker';
import Textarea from '~/ui/components/inputs/Textarea';

import {
  exemptionResolver,
  getDefaultValues,
  IExemptionFormContext,
  IExemptionFormValues,
} from '~/ui/pages/Clients/reusable/CommunityEngagement/helpers/exemptionForm';
import useCeDocuments from '~/ui/pages/Clients/reusable/CommunityEngagement/hooks/useCeDocuments';
import AutomaticChip from '../AutomaticChip';
import CeDocumentsField from '../CeDocumentsField';

import modalStyles from '~/ui/pages/Clients/reusable/CommunityEngagement/CeModal.module.scss';

import styles from './ExemptionModal.module.scss';

interface IExemptionModalProps {
  month: ICeMonthRequest;
  categories: ICeExemptionCategory[];
  /** `null` adds a new exemption. */
  exemption: ICeExemption | null;
  onClose: () => void;
  onSaved: () => void;
}

const ExemptionModal = ({
  month,
  categories,
  exemption,
  onClose,
  onSaved,
}: IExemptionModalProps): ReactElement => {
  const title = exemption ? 'Edit Exemption' : 'Add Exemption';
  const isAutomatic = Boolean(exemption?.isAutomatic);
  const defaultValues = getDefaultValues(exemption, month);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);

  const { onCreateExemption, onUpdateExemption } = useStoreActions(
    actions => actions.communityEngagement,
  );
  const { showError, showNotify } = useStoreActions(actions => actions.snackbar);
  const documents = useCeDocuments(exemption?.documents ?? []);

  const {
    control,
    register,
    handleSubmit,
    watch,
    trigger,
    setError,
    formState: { errors, isSubmitted, isSubmitting },
  } = useForm<IExemptionFormValues, IExemptionFormContext>({
    defaultValues,
    resolver: exemptionResolver,
    context: { isAutomatic },
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const currentValues = watch();
  const { exemptionCategoryId: categoryId, effectiveDate } = currentValues;
  const isDirty = !isEqual(currentValues, defaultValues) || documents.isDirty;
  const hasErrors = Object.keys(errors).length > 0;
  const expirationReferenceDate = parseISO(effectiveDate);

  const checkedEffectiveDate = useRef(effectiveDate);
  useEffect(() => {
    if (checkedEffectiveDate.current === effectiveDate) return;
    checkedEffectiveDate.current = effectiveDate;
    if (isSubmitted) trigger('expirationDate');
  }, [effectiveDate, isSubmitted, trigger]);

  const activeOptions = categories.map(({ id, name }) => ({ label: name, value: id }));
  const savedCategory = exemption?.category;
  const showsArchivedCategory =
    savedCategory?.id === categoryId && !categories.some(({ id }) => id === categoryId);
  const options = showsArchivedCategory
    ? [{ label: savedCategory.name, value: savedCategory.id, isDisabled: true }, ...activeOptions]
    : activeOptions;

  const selectedCategory = showsArchivedCategory
    ? savedCategory
    : categories.find(({ id }) => id === categoryId);
  const renewalGuidance = selectedCategory?.renewalGuidance;

  const requestClose = () => {
    if (isDirty) setIsConfirmingDiscard(true);
    else onClose();
  };

  const onSubmit = async (values: IExemptionFormValues) => {
    const common = {
      exemptionCategoryId: Number(values.exemptionCategoryId),
      effectiveDate: toDay(values.effectiveDate) ?? values.effectiveDate,
      expirationDate: toDay(values.expirationDate) ?? values.expirationDate,
      comment: values.comment.trim() || null,
    };

    try {
      if (exemption) {
        await onUpdateExemption({
          ...month,
          exemptionId: exemption.id,
          exemption: { ...common, documents: documents.toUpdateRequest() },
        });
      } else {
        await onCreateExemption({
          ...month,
          exemption: { ...common, documents: documents.toCreateRequest() },
        });
      }
      showNotify({ message: exemption ? 'Exemption updated' : 'Exemption added' });
      onSaved();
      onClose();
    } catch (error) {
      if (extractErrorCode(error) === CeErrorCode.ExemptionOverlap) {
        setError('expirationDate', { type: 'server', message: extractErrorMessage(error) });
      } else {
        showError(extractErrorMessage(error));
      }
    }
  };

  return (
    <>
      <Modal
        open
        preventOutsideClick
        maxWidth="sm"
        fullWidth
        className={modalStyles.paper}
        onClose={requestClose}
      >
        <section className={modalStyles.container}>
          <header className={modalStyles.header}>
            <IconButton aria-label="Close" className={modalStyles.close} onClick={requestClose}>
              <CloseIcon />
            </IconButton>
            <h2 className={modalStyles.title}>{title}</h2>
          </header>
          <form className={modalStyles.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={modalStyles.content}>
              <FormSelect
                control={control}
                name="exemptionCategoryId"
                label="Exemption Category"
                options={options}
                disabled={isAutomatic}
                renderOption={option =>
                  isAutomatic && option.value === savedCategory?.id ? (
                    <span className={styles.automaticCategory}>
                      {option.label}
                      <AutomaticChip />
                    </span>
                  ) : (
                    option.label
                  )
                }
              />
              <DatePicker
                name="effectiveDate"
                label="Exemption Effective Date"
                openTo="date"
                disabled={isAutomatic}
                control={control}
                errors={errors}
              />
              <div>
                <DatePicker
                  name="expirationDate"
                  label="Exemption Expiration Date"
                  openTo="date"
                  disabled={isAutomatic}
                  referenceDate={
                    isValid(expirationReferenceDate) ? expirationReferenceDate : undefined
                  }
                  control={control}
                  errors={errors}
                />
                {renewalGuidance && <p className={styles.hint}>{renewalGuidance}</p>}
              </div>
              <Textarea
                name="comment"
                label="Process Comments"
                placeholder="Add Comments"
                register={register}
                errors={errors}
              />
              <CeDocumentsField documents={documents} />
            </div>
            <footer className={modalStyles.actions}>
              <Button type="button" color="primary" variant="outlined" onClick={requestClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={isSubmitting || documents.isBusy || !isDirty || hasErrors}
                isLoading={isSubmitting}
              >
                Save
              </Button>
            </footer>
          </form>
        </section>
      </Modal>
      {isConfirmingDiscard && (
        <ConfirmModal
          description={`Are you sure you want to cancel “${title}”? All the changes will be discarded.`}
          onClose={() => setIsConfirmingDiscard(false)}
          onConfirm={onClose}
        />
      )}
    </>
  );
};

export default ExemptionModal;
